import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, of, switchMap, withLatestFrom } from 'rxjs';
import * as AgencyDetailsActions from './agency-details-store.actions';
import { BaseAgencyDetailsService } from '../services/agency-details.service';
import {
  AgencyDetails,
  AgencyDetailsResponse,
  AgencyDetailsUpdateObjectResponse,
  AgencySettings,
  AgencySettingsResponse,
  Contact,
  StorageInfo,
} from '../models/agency-details.models';
import { Category, CategoryResponse } from '../models/agency-category.models';
import { AgencyDetailsFacade } from './agency-details-store.facade';
import { aaDataResponse } from '@shared/data-access';
import { UploadImageService } from '@shared/feature/upload-file';
import { NotificationService } from '@shared/feature/notification-handling';
import { CadCategory } from '@shared/utils/category';
import { HttpErrorResponse } from '@angular/common/http';
import { ActionType, AgencyActionResult } from '../models/agency-action-result.models';
import { ModalService } from '@shared/feature/modal';
import { TranslationFromJsonService } from '@shared/feature/translate';

@Injectable()
export class AgencyDetailsEffects {
  fetchAgencySettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgencyDetailsActions.fetchAgencySettings),
      switchMap(() =>
        this.service.fetchSettings().pipe(
          map((response: AgencySettingsResponse) => {
            const settings = new AgencySettings(
              response.CanDownloadClient,
              response.CanSelectiveUpload,
              response.IsLoginPinRequired,
              response.UseDownloadReason,
              response.DownloadReasonMinLength,
              response.UseMilitaryTime,
              response.EvidenceLossResilience,
              response.LocationsEnabled
            );

            return AgencyDetailsActions.fetchAgencySettingsSuccess({ settings });
          }),
          catchError(() => of(AgencyDetailsActions.fetchAgencySettingsFailure()))
        )
      )
    )
  );

  fetchAgencyDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgencyDetailsActions.fetchAgencyDetails),
      switchMap(() =>
        this.service.fetchAgencyDetails().pipe(
          map((response: AgencyDetailsResponse) => {
            const agency = this.transformAgencyDetailsResponse(response);
            agency.uidIdentifier = response.uidIdentifier;

            return agency;
          })
        )
      ),
      map((response) => AgencyDetailsActions.fetchAgencyDetailsSuccess({ agencyDetails: response }))
    )
  );

  fetchAgencyCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgencyDetailsActions.fetchAgencyCategories),
      switchMap(() => this.facade.getAgencyUid()),
      switchMap((uid: string) => this.service.fetchCategories(uid)),
      map((response: aaDataResponse<CategoryResponse>) =>
        response.aaData.map(
          (category) =>
            new Category(
              category.Name,
              category.RetainDays,
              category.UseRetainDays,
              category.IsArchived,
              category.IsDefault,
              category.CadCategories.map((x) => new CadCategory(x.Name, x.Uid)),
              category.AppliedOnFiles,
              category.UID,
              category.Description
            )
        )
      ),
      map((categories) => AgencyDetailsActions.fetchAgencyCategoriesSuccess({ categories }))
    )
  );

  updateAgencyPhoto$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyDetailsActions.updateAgencyPhoto),
      exhaustMap((action) => {
        return this.uploadImageService.uploadImageApi(action.photo).pipe(
          map((newUrl) => {
            this.notificationService.addSuccess(this.translateFromJsonService.instant('customer-photo-successfully-changed'));

            return AgencyDetailsActions.updateAgencyPhotoSuccess({ newUrl: newUrl });
          }),
          catchError(() => {
            this.notificationService.addError(this.translateFromJsonService.instant('something-went-wrong'));

            return of(AgencyDetailsActions.updateAgencyPhotoFailure());
          })
        );
      })
    );
  });

  updateAgencyDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyDetailsActions.updateAgencyDetails),
      exhaustMap((action) => {
        return this.service.editAgencyDetails(action.details).pipe(
          map((response: AgencyDetailsUpdateObjectResponse) => {
            const agency = this.transformAgencyDetailsResponse(response);
            this.notificationService.addSuccess(this.translateFromJsonService.instant('customer-details-successfully-changed'));
            this.modalService.closeAllModals();

            return AgencyDetailsActions.updateAgencyDetailsSuccess({ details: agency });
          }),
          catchError((error: HttpErrorResponse) =>
            of(AgencyDetailsActions.updateAgencyDetailsActionResult({ actionResult: new AgencyActionResult(ActionType.UpdateAgencyDetails, error, false) }))
          )
        );
      })
    );
  });

  updatePointOfContact$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyDetailsActions.updatePointOfContact),
      exhaustMap((action) => {
        return this.service.editPointOfContact(action.details).pipe(
          map((response: AgencyDetailsUpdateObjectResponse) => {
            const agency = this.transformAgencyDetailsResponse(response);
            this.notificationService.addSuccess(this.translateFromJsonService.instant('customer-has-been-updated'));

            return agency;
          })
        );
      }),
      map((response) => AgencyDetailsActions.updatePointOfContactSuccess({ details: response }))
    );
  });

  updateAgencySettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyDetailsActions.updateAgencySettings),
      withLatestFrom(this.facade.getAgencySettings()),
      exhaustMap(([action, currentSettings]) => {
        return this.service.editAgencySettings(action.settings).pipe(
          map(() => {
            this.notificationService.addSuccess(this.translateFromJsonService.instant('settings-changed-successfully'));
            const agencySettings = new AgencySettings(
              action.settings.CanDownloadClient,
              action.settings.CanSelectiveUpload,
              action.settings.IsLoginPinRequired,
              action.settings.UseDownloadReason,
              action.settings.DownloadReasonMinLength,
              action.settings.UseMilitaryTime,
              action.settings.EvidenceLossResilience,
              currentSettings.locationsEnabled
            );

            return AgencyDetailsActions.updateAgencySettingsSuccess({ settings: agencySettings });
          })
        );
      })
    );
  });

  addNewAgencyCategory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyDetailsActions.addNewAgencyCategory),
      exhaustMap((action) => {
        return this.service.addNewAgencyCategory(action.category).pipe(
          map((response: Category) => {
            this.notificationService.addSuccess(this.translateFromJsonService.instant('category-created'));

            return AgencyDetailsActions.addNewAgencyCategorySuccess({ category: response });
          })
        );
      })
    );
  });

  updateAgencyCategory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyDetailsActions.updateAgencyCategory),
      exhaustMap((action) => {
        return this.service.editAgencyCategory(action.uid, action.category).pipe(
          map((response: Category) => {
            this.notificationService.addSuccess(this.translateFromJsonService.instant('changes-saved'));
            this.modalService.closeAllModals();

            return AgencyDetailsActions.updateAgencyCategorySuccess({ category: response });
          }),
          catchError((error: HttpErrorResponse) =>
            of(AgencyDetailsActions.updateAgencyCategoryActionResult({ actionResult: new AgencyActionResult(ActionType.UpdateAgencyCategory, error, false) }))
          )
        );
      })
    );
  });

  deleteAgencyCategory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyDetailsActions.deleteAgencyCategory),
      exhaustMap((action) => {
        return this.service.deleteAgencyCategory(action.uid).pipe(
          map(() => {
            this.notificationService.addSuccess(this.translateFromJsonService.instant('category-deleted'));

            return AgencyDetailsActions.deleteAgencyCategorySuccess({ uid: action.uid });
          })
        );
      })
    );
  });

  updateOnvifSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyDetailsActions.updateOnvifSettings),
      exhaustMap((action) => {
        return this.service.updateOnvifSettings(action.settings).pipe(
          map(() => {
            this.notificationService.addSuccess(this.translateFromJsonService.instant('settings-changed-successfully'));
            this.modalService.closeAllModals();

            return AgencyDetailsActions.updateOnvifSettingsSuccess();
          }),
          catchError(() => {
            this.notificationService.addError(this.translateFromJsonService.instant('something-went-wrong'));
            return of(AgencyDetailsActions.updateOnvifSettingsFailure());
          })
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private service: BaseAgencyDetailsService,
    private facade: AgencyDetailsFacade,
    private uploadImageService: UploadImageService,
    private notificationService: NotificationService,
    private modalService: ModalService,
    private translateFromJsonService: TranslationFromJsonService
  ) {}

  private transformAgencyDetailsResponse(response: AgencyDetailsResponse): AgencyDetails {
    return new AgencyDetails(
      response.UID,
      response.Name,
      response.ShortName,
      response.PictureFullPath,
      response.CanDownloadClient,
      response.CanSelectiveUpload,
      response.IsLoginPinRequired,
      response.CanDeleteFiles,
      new StorageInfo(response.AwsInfo.AccessKeyId, response.AwsInfo.RegionName, response.AwsInfo.SecretAccessKey),
      response.Phone,
      response.EmailAddress,
      response.Address,
      response.CaseDownloadAvailableDays,
      new Contact(response.PrimaryContactEmail, response.PrimaryContactName, response.PrimaryContactPhone, response.PrimaryContactNotificationPeriod),
      new Contact(response.SecondaryContactEmail, response.SecondaryContactName, response.SecondaryContactPhone, response.SecondaryContactNotificationPeriod),
      response.ManualDeleteRetainDays,
      response.CanUseCad,
      response.CanUseSelfRedaction
    );
  }
}
